import { CalendarToday, TaskAlt as TaskAltIcon } from '@mui/icons-material';
import {
  hasLaborCreateAccess,
  hasLaborTeamViewAccess,
  Permissions,
  useScopeChecker,
} from 'src/rbac';
import { Button, Chip, Modal, Paper, Skeleton, SvgIcon, Typography } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import { Box, Container, Stack } from '@mui/system';
import moment from 'moment';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PlusIcon from '@untitled-ui/icons-react/build/esm/Plus';
import { useSettings } from 'src/hooks/use-settings';
import {
  getAllLaborCodes,
  getAllLaborHours,
  getPayPeriodByDateAction,
  resetAddLaborHoursAction,
  resetDeleteLaborHoursAction,
  resetEditLaborHoursAction,
  resetPayPeriodByDateAction,
} from 'src/redux/actions/laborHourActions';
import { Seo } from '../seo';
import SucessModal from '../sucess-modal';
import EditLaborManagement from './edit-labor-management';
import LaborHourManagementTable from './laber-hour-management-table';
import { convertIntoIsoFormat, toISOWithOffset } from 'src/utils/convertDateToSelectedTimeZone';
import useCurrentUser from 'src/hooks/use-current-user';
import { Link } from 'react-router-dom';
import { BasicDateCalendar } from 'src/layouts/components/CustomDatePicker';
import { paths } from 'src/paths';

const PAY_PERIOD_STATUS: Record<string, { label: string; bg: string; color: string }> = {
  OPEN: { label: 'OPEN', bg: '#FEE2E2', color: '#B91C1C' },
  PARTIALLY_APPROVED: { label: 'PARTIALLY APPROVED', bg: '#FDF6B2', color: '#8E4B10' },
  APPROVED: { label: 'APPROVED', bg: '#DEF7EC', color: '#03543F' },
};

const LaborHourManagement = () => {
  const settings = useSettings();
  const dispatch = useDispatch();

  const todayDate = useMemo(() => new Date(), []);
  const getAllLaborHrs = useSelector((state: any) => state.getLaborHoursReducer);
  const payPeriodByDate = useSelector((state: any) => state.getPayPeriodByDateReducer);
  const getFacilityTimeZone = localStorage.getItem('facility_timezone') || '';
  const has = useScopeChecker();
  const [providerId] = useCurrentUser(['providerId']);

  const canViewAllLabor = hasLaborTeamViewAccess(has);
  // THIN-8096: Edit Own / Edit Team Labor are write permissions in their own right —
  // checking only the legacy Create Labor Management permission left a user holding
  // just Edit Own Labor on the page with a permanently disabled Add New button.
  const canAddLaborEntry = hasLaborCreateAccess(has);
  const [selectedDate, setSelectedDate] = useState<any>(() => todayDate);
  const [openCalender, setOpenCalender] = useState(false);
  const calenderRef = useRef<HTMLDivElement | null>(null);
  const [openEditHours, setOpenEditHours] = useState(false);
  const [openHoursType, setOpenHoursType] = useState({
    type: '',
    value: '',
  });

  const [createPtoRequestError, setCreatePtoRequestError] = useState(false);
  const [sucessFullRequest, setSucessFullRequest] = useState(false);

  const [rowPerPage, setRowPerPage] = useState(10);
  const [page, setPage] = useState(0);

  const [successModalTitle, setSuccessModalTitle] = useState({
    title: '',
    message: '',
  });
  const addLaborHrsResponse = useSelector((state: any) => state.addLaborHoursReducer);
  const editLaborHrsResponse = useSelector((state: any) => state.editLaborHoursReducer);
  const deleteLaborHrsResponse = useSelector((state: any) => state.deleteLaborHoursReducer);
  useEffect(() => {
    dispatch(getAllLaborCodes());
    return () => {
      dispatch(resetPayPeriodByDateAction());
    };
  }, [dispatch]);

  useEffect(() => {
    if (!openCalender) return;
    const handleClickOutside = (event: any) => {
      if (calenderRef.current && !calenderRef.current.contains(event.target)) {
        setOpenCalender(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [openCalender]);

  const handleRequestLabor = (item: any) => {
    setOpenHoursType((prev) => ({
      ...prev,
      type: 'EDIT',
      value: item,
    }));
    setOpenEditHours(true);
  };

  const closePTOModal = () => {
    setOpenEditHours(false);
  };

  const handleAddNewData = () => {
    setOpenHoursType((prev) => ({
      ...prev,
      type: 'ADD',
      value: '',
    }));
    setOpenEditHours(true);
  };

  useEffect(() => {
    dispatch(getAllLaborHours(laborHoursParamsRef.current()));
  }, [page, rowPerPage, dispatch]);

  useEffect(() => {
    dispatch(getAllLaborHours(laborHoursParamsRef.current({ page: 0, pageSize: 10 })));
    dispatch(getPayPeriodByDateAction({ date: moment(selectedDate).format('YYYY-MM-DD') }));
    setRowPerPage(10);
    setPage(0);
  }, [selectedDate, dispatch]);

  const totalEntries = getAllLaborHrs?.response?.totalElements || 0;

  const jsDate = moment(selectedDate, 'DD-MM-YYYY').toDate();

  const selectedCycle = payPeriodByDate?.cycle || null;
  const isLaborValidationEnabled = useSelector(
    (state: any) => state?.getLaborValidationToggleReducer?.response?.active
  );

  const payPeriodStatus = selectedCycle?.status;
  // An unrecognised status still shows, just with the neutral treatment.
  const payPeriodStatusConfig = PAY_PERIOD_STATUS[String(payPeriodStatus || '').toUpperCase()] || {
    label: String(payPeriodStatus || '')
      .toUpperCase()
      .replace(/_/g, ' '),
    bg: '#F1F5F9',
    color: '#475569',
  };
  const isPeriodApproved = String(payPeriodStatus || '').toUpperCase() === 'APPROVED';
  const payPeriodLabel =
    selectedCycle?.startDate && selectedCycle?.endDate
      ? `${moment(selectedCycle.startDate, 'YYYY-MM-DD').format('MMM D')} – ${moment(
          selectedCycle.endDate,
          'YYYY-MM-DD'
        ).format('MMM D, YYYY')}`
      : '';

  const laborHoursParams = (overrides?: { page?: number; pageSize?: number }) => ({
    date: convertIntoIsoFormat(toISOWithOffset(selectedDate)),
    page: overrides?.page ?? page,
    pageSize: overrides?.pageSize ?? rowPerPage,
    zoneId: getFacilityTimeZone,
  });

  // The refetch effects below fire on their own narrow triggers (paging, a new date, a
  // completed write). Reading the params through a ref keeps them off the params identity
  // — which changes every render — without letting them read stale values.
  const laborHoursParamsRef = useRef(laborHoursParams);
  laborHoursParamsRef.current = laborHoursParams;

  const handleChangeDate = (value: any) => {
    if (!value) return;
    setSelectedDate(value);
    setOpenCalender(false);
  };

  useEffect(() => {
    if (!addLaborHrsResponse?.response && !addLaborHrsResponse?.isAddLaborHours) return;
    if (!addLaborHrsResponse?.isAddLaborHours && addLaborHrsResponse?.response !== null) {
      setSuccessModalTitle((prev) => ({
        ...prev,
        title: 'Error',
        message: addLaborHrsResponse?.response?.message ?? 'Error while Adding Labor management',
      }));
      dispatch(resetAddLaborHoursAction());
      setOpenEditHours(false);
      setOpenHoursType({ type: '', value: '' });
      setCreatePtoRequestError(true);
      setSucessFullRequest(true);
      return;
    }
    if (addLaborHrsResponse?.isAddLaborHours && addLaborHrsResponse?.response?.status == 200) {
      setSuccessModalTitle((prev) => ({
        ...prev,
        title: 'Labor management added successfully !',
        message: '',
      }));
      setOpenEditHours(false);
      setCreatePtoRequestError(false);
      setOpenHoursType({ type: '', value: '' });
      dispatch(getAllLaborHours(laborHoursParamsRef.current()));
      dispatch(resetAddLaborHoursAction());
      setSucessFullRequest(true);

      return;
    }
  }, [addLaborHrsResponse, dispatch]);

  useEffect(() => {
    if (!editLaborHrsResponse?.response && !editLaborHrsResponse?.isEditLaborHours) return;
    if (!editLaborHrsResponse?.isEditLaborHours && editLaborHrsResponse?.response !== null) {
      setSuccessModalTitle((prev) => ({
        ...prev,
        title: 'Error',
        message: editLaborHrsResponse?.response?.message ?? 'Error while updating labor management',
      }));
      dispatch(resetEditLaborHoursAction());
      setOpenEditHours(false);
      setOpenHoursType({ type: '', value: '' });
      setCreatePtoRequestError(true);
      setSucessFullRequest(true);
      return;
    }
    if (editLaborHrsResponse?.isEditLaborHours && editLaborHrsResponse?.response?.status == 200) {
      setSuccessModalTitle((prev) => ({
        ...prev,
        title: 'Labor management updated successfully !',
        message: '',
      }));
      setOpenEditHours(false);
      setCreatePtoRequestError(false);
      setOpenHoursType({ type: '', value: '' });
      dispatch(getAllLaborHours(laborHoursParamsRef.current()));
      dispatch(resetEditLaborHoursAction());
      setSucessFullRequest(true);

      return;
    }
  }, [editLaborHrsResponse, dispatch]);

  const [openDeleteModal, setOpenDeleteModal] = useState(false);

  useEffect(() => {
    if (!deleteLaborHrsResponse?.response && !deleteLaborHrsResponse?.isDeletedLaborHours) return;
    if (!deleteLaborHrsResponse?.isDeletedLaborHours && deleteLaborHrsResponse?.response !== null) {
      setSuccessModalTitle((prev) => ({
        ...prev,
        title: 'Error',
        message:
          deleteLaborHrsResponse?.response?.message ??
          'Error while deleting labor management entry',
      }));
      setOpenDeleteModal(false);
      setCreatePtoRequestError(true);
      setSucessFullRequest(true);
      dispatch(resetDeleteLaborHoursAction());
      return;
    }
    if (
      deleteLaborHrsResponse?.isDeletedLaborHours &&
      deleteLaborHrsResponse?.response?.status == 200
    ) {
      setSuccessModalTitle((prev) => ({
        ...prev,
        title: 'Labor management entry deleted successfully !',
        message: '',
      }));
      setOpenDeleteModal(false);
      setCreatePtoRequestError(false);
      dispatch(getAllLaborHours(laborHoursParamsRef.current()));
      dispatch(resetDeleteLaborHoursAction());
      setSucessFullRequest(true);

      return;
    }
  }, [deleteLaborHrsResponse, dispatch]);

  const closeSuccessModal = () => {
    setSucessFullRequest(false);
  };

  const onRowsPerPageChange = (event: any) => {
    const newRowsPerPage = event.target.value;
    setRowPerPage(newRowsPerPage);
    setPage(0);
  };

  const onPageChange = (_event: any, newPage: number) => {
    setPage(newPage);
  };

  return (
    <>
      <Seo title="Restore-Insights: Lobar hour Management" />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          py: 4,
          px: 5,
        }}
      >
        <Container maxWidth={settings.stretch ? false : 'xl'}>
          <Grid
            container
            disableEqualOverflow
            spacing={{
              xs: 3,
              lg: 4,
            }}
          >
            <Grid xs={12}>
              <Stack
                direction="row"
                alignItems="center"
                spacing={4}
              >
                <Stack gap="1rem">
                  <Typography variant="h4">Labor Hour Management</Typography>
                </Stack>

                <Stack ref={calenderRef}>
                  <Button
                    variant="outlined"
                    onClick={() => setOpenCalender((prev) => !prev)}
                    startIcon={
                      <SvgIcon>
                        <CalendarToday />
                      </SvgIcon>
                    }
                    sx={{
                      background: '#fff',
                      borderRadius: '12px',
                      border: '1px solid rgba(17, 25, 39, 0.16)',
                      color: '#384250',
                      '&:hover': {
                        border: '1px solid rgba(17, 25, 39, 0.16)',
                      },
                    }}
                  >
                    {moment(jsDate).format('MMM D, YYYY')}
                    {moment(jsDate).isSame(todayDate, 'day') ? ' (Today)' : ''}
                  </Button>
                  {openCalender && (
                    <BasicDateCalendar
                      value={selectedDate}
                      handleChangeDate={handleChangeDate}
                    />
                  )}
                </Stack>
              </Stack>

              {payPeriodLabel && isLaborValidationEnabled && (
                <Stack
                  direction="row"
                  alignItems="center"
                  gap={1}
                  sx={{ mt: 2 }}
                  flexWrap="wrap"
                >
                  <Typography
                    color="text.secondary"
                    variant="body2"
                  >
                    Pay period:
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 600 }}
                  >
                    {payPeriodLabel}
                  </Typography>
                  {payPeriodStatus && (
                    <Chip
                      label={payPeriodStatusConfig.label}
                      size="small"
                      sx={{
                        height: 22,
                        fontSize: 12,
                        fontWeight: 700,
                        borderRadius: '6px',
                        bgcolor: payPeriodStatusConfig.bg,
                        color: payPeriodStatusConfig.color,
                        '& .MuiChip-label': { px: 1 },
                      }}
                    />
                  )}
                  <Typography
                    color="text.secondary"
                    variant="body2"
                  >
                    · approved entries are locked for therapists
                  </Typography>
                </Stack>
              )}

              {/* Approval is a status marker, not a lock — the period simply reverts to
                  Partially Approved when someone edits into it. */}
              {isPeriodApproved && isLaborValidationEnabled && (
                <Paper
                  variant="outlined"
                  sx={{
                    mt: 2,
                    p: 2,
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1.5,
                    bgcolor: '#ECFDF5',
                    border: '1px solid #A7F3D0',
                    borderRadius: '10px',
                  }}
                >
                  <TaskAltIcon sx={{ color: '#059669', fontSize: 22, mt: 0.25 }} />
                  <Typography sx={{ fontSize: 13, color: '#065F46' }}>
                    <strong>This date falls in an approved pay period.</strong> Approval is a status
                    marker, not a lock. Editing or adding an entry returns the period to{' '}
                    <strong>Partially Approved</strong> — re-approve it in{' '}
                    <Link
                      to={`${paths.laborManagement.index}/${paths.laborManagement.laborReportAndApproval}`}
                      style={{ color: '#065F46', fontWeight: 700 }}
                    >
                      Labor Report &amp; Approval
                    </Link>{' '}
                    when done.
                  </Typography>
                </Paper>
              )}
            </Grid>
            <Grid
              xs={12}
              pt={1}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography variant="h6">
                    {canViewAllLabor ? "Today's Therapists" : 'My Labor Entries'}
                  </Typography>
                  {getAllLaborHrs?.loading ? (
                    <Box sx={{ width: 100, ml: 3 }}>
                      <Typography
                        width={'100%'}
                        variant="h4"
                      >
                        <Skeleton animation="pulse" />
                      </Typography>
                    </Box>
                  ) : (
                    <Chip
                      label={`${totalEntries} ${
                        canViewAllLabor ? 'Therapists' : `Entr${totalEntries === 1 ? 'y' : 'ies'}`
                      }`}
                      variant="outlined"
                      sx={{ marginLeft: '10px' }}
                    />
                  )}
                </Box>

                <Box
                  sx={{
                    margin: '0px 11px',
                  }}
                >
                  <Button
                    startIcon={
                      <SvgIcon>
                        <PlusIcon />
                      </SvgIcon>
                    }
                    variant="outlined"
                    onClick={handleAddNewData}
                    disabled={!canAddLaborEntry}
                  >
                    Add New
                  </Button>
                  {isPeriodApproved && (
                    <Typography
                      sx={{ fontSize: 12, color: '#94A3B8', mt: 0.75, textAlign: 'right' }}
                    >
                      Adding an entry requires overriding the period approval
                    </Typography>
                  )}
                </Box>
              </Box>
              <Stack>
                <LaborHourManagementTable
                  handleRequestLabor={handleRequestLabor}
                  handleAddNewData={handleAddNewData}
                  onRowsPerPageChange={onRowsPerPageChange}
                  onPageChange={onPageChange}
                  page={page}
                  rowPerPage={rowPerPage}
                  openDeleteModal={openDeleteModal}
                  setOpenDeleteModal={setOpenDeleteModal}
                />
              </Stack>
            </Grid>
            <Modal
              open={openEditHours}
              style={{ position: 'relative' }}
            >
              <EditLaborManagement
                close={closePTOModal}
                openHoursType={openHoursType}
                selectedDate={selectedDate}
              />
            </Modal>
            <Modal
              onClose={closeSuccessModal}
              open={sucessFullRequest}
            >
              <SucessModal
                close={closeSuccessModal}
                isSucess={!createPtoRequestError}
                title={successModalTitle.title}
                message={successModalTitle.message}
              />
            </Modal>
          </Grid>
        </Container>
      </Box>
    </>
  );
};

export default LaborHourManagement;
